import { useEffect, useMemo, useState } from 'react';
import type { DirectoryUser, TenantSettingsService, TenantUser } from '../TenantSettingsService';

export interface IdpSearchResult {
  results: DirectoryUser[];
  searching: boolean;
  idpOnlyUsers: DirectoryUser[];
}

export function useIdpSearch(
  service: TenantSettingsService,
  query: string,
  localUsers: TenantUser[],
): IdpSearchResult {
  const [results, setResults] = useState<DirectoryUser[]>([]);
  // Start as true when directory search is available so callers don't flash
  // an empty state before the initial load completes.
  const [searching, setSearching] = useState(() => !!service.searchDirectory);

  useEffect(() => {
    if (!service.searchDirectory) {
      setResults([]);
      setSearching(false);
      return;
    }
    const q = query.trim();
    setSearching(true);
    // No delay on mount (empty query); debounce typed queries.
    const delay = q ? 400 : 0;
    const timer = setTimeout(() => {
      void service
        .searchDirectory?.(q)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, delay);
    return () => {
      clearTimeout(timer);
      setSearching(false);
    };
  }, [query, service]);

  const localEmailSet = useMemo(
    () => new Set(localUsers.map((u) => u.email?.toLowerCase()).filter(Boolean)),
    [localUsers],
  );

  const idpOnlyUsers = useMemo(
    () => results.filter((d) => !localEmailSet.has(d.email?.toLowerCase() ?? '')),
    [results, localEmailSet],
  );

  return { results, searching, idpOnlyUsers };
}
